/**
 * Gift Manager — metaobject-driven gift-with-purchase engine.
 * Reads config from #gift-campaigns-config JSON, evaluates conditions
 * against the live cart, and auto-adds / removes / picks gifts.
 */
(function () {
  'use strict';

  var CHECKMARK_SVG =
    '<svg class="gift-picker__checkmark" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<circle cx="8" cy="8" r="8" fill="#2d6a2d"/>' +
      '<path d="M4.5 8.5L7 11L11.5 5.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg> ';

  var TRUCK_SVG =
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M3 6.5h10.5v9H3v-9z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<path d="M13.5 9.5H17l3 3v3h-6.5v-6z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<circle cx="7" cy="17" r="1.6" stroke="currentColor" stroke-width="1.5"/>' +
      '<circle cx="17" cy="17" r="1.6" stroke="currentColor" stroke-width="1.5"/>' +
    '</svg>';

  var GIFT_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">' +
      '<rect x="3" y="8" width="18" height="4" rx="1"/>' +
      '<path d="M12 8v13"/>' +
      '<path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/>' +
      '<path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/>' +
    '</svg>';

  class GiftManager {
    constructor() {
      this.campaigns = [];
      this.campaignGroups = {};
      this._queue = Promise.resolve();
      this._evaluating = false;
      this._giftOpsPending = false;
      this._debounceTimer = null;
      this._lastCartToken = null;
      this._origUpdateCheckout = null;
      this._suppressCartUpdates = false;
      // Eligibility is locked to the customer's own (non-gift) cart state so the
      // gift's presence can't feed back into its own decision (prevents the
      // add/remove oscillation when discounts reallocate). See evaluate().
      this._decidedNonGiftFp = null;
      this._decisions = null;
      this._lastMetrics = null;
      // Campaigns whose gift add.js failed transiently (e.g. 429) for the
      // current non-gift cart state — retried on the next real cart change.
      this._failedAdds = {};
      // Campaigns whose gift add.js failed permanently (e.g. 422 out-of-stock /
      // invalid variant). Not retried for the rest of the session, so we don't
      // waste a doomed request on every cart change. Reset on page reload.
      this._deadGiftCampaigns = {};
      this.init();
    }

    /* ------------------------------------------------------------------ */
    /*  Initialisation                                                     */
    /* ------------------------------------------------------------------ */

    init() {
      const configEl = document.getElementById('gift-campaigns-config');
      this.freeShipping = this._readFreeShippingConfig();
      this.excludedVendors = this._readExcludedVendors();
      this.coldChain = this._readColdChainConfig();

      if (configEl) {
        try {
          this.campaigns = JSON.parse(configEl.textContent);
        } catch (e) {
          this.campaigns = [];
        }
      }

      var hasCampaigns = this.campaigns.length > 0;
      var hasFreeShipping = !!(this.freeShipping && this.freeShipping.show);

      // Boot when there are gift campaigns OR a free-shipping tier to show.
      // Free-shipping-only mode renders the bar but does no gift add/remove.
      if (!hasCampaigns && !hasFreeShipping) return;

      // Group by campaignName for deduplication (empty {} in FS-only mode).
      this.campaignGroups = {};
      this.giftVariants = {};
      this.campaigns.forEach(function (c) {
        var name = c.campaignName;
        if (!this.campaignGroups[name]) this.campaignGroups[name] = [];
        this.campaignGroups[name].push(c);

        // Variant id -> campaign, the SECOND way a gift line can be recognised.
        // See campaignForLine for why the property alone is not enough.
        (c.giftOptions || []).forEach(function (product) {
          (product.variants || []).forEach(function (v) {
            var id = v.numericId || this.extractNumericId(String(v.id));
            if (id) this.giftVariants[String(id)] = name;
          }, this);
        }, this);
      }, this);

      // Restore the last locked decision from a previous page so an unchanged
      // cart skips re-deciding on load. Purely an optimisation — evaluating in
      // place already keeps an already-eligible gift across reloads.
      this._restoreDecision();

      // Prevent old React gift system errors
      window.updateCartGiftsStatus = this.onCartUpdate.bind(this);
      window.updateCartGiftsBannerStatus = function () {};

      // Wrap updateCheckoutButtonState to block premature enabling during gift ops.
      var origFn = window.updateCheckoutButtonState;
      if (typeof origFn === 'function') {
        this._origUpdateCheckout = origFn;
        var self = this;
        window.updateCheckoutButtonState = function (cartItems, cartFormType) {
          if (self._giftOpsPending) return;
          return origFn.call(this, cartItems, cartFormType);
        };
      }

      // Subscribe to theme cart events
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      // Hook into updateCartFormVisibility — cart.js calls this on every
      // successful cart change, the most reliable signal.
      var origCartFormVis = window.updateCartFormVisibility;
      if (typeof origCartFormVis === 'function') {
        var self = this;
        window.updateCartFormVisibility = function () {
          origCartFormVis.apply(this, arguments);
          self.onCartUpdate();
        };
      }

      if (hasCampaigns) {
        this.interceptCheckout();

        // Hold checkout button disabled until initial evaluation completes.
        this._giftOpsPending = true;
        document.querySelectorAll('.cart__checkout-button').forEach(function (btn) {
          btn.setAttribute('disabled', 'disabled');
        });
      }

      // Prefer the Liquid seed for this FIRST evaluation instead of firing a
      // load-time /cart.js (this file is hand-maintained vanilla outside the
      // webpack graph — see js/components/cart-state.js — so it reads the broker
      // off window.__gvCartState, exposed there for exactly this, rather than
      // importing). Reuses the SAME _pendingCart hand-off evaluate() already uses
      // for Dawn's own fresh-cart hooks (see onCartUpdate below), so both paths
      // converge on a real cart OBJECT — never a synthetic fetch Response — and
      // evaluate()'s own fetchCart() fallback still runs unmodified whenever no
      // seed/broker is available (including every later re-evaluation, which
      // deliberately keeps reading fresh — see the comment in evaluate()).
      if (window.__gvCartState && typeof window.__gvCartState.getCart === 'function') {
        var seededCart = window.__gvCartState.getCart();
        if (seededCart) this._pendingCart = seededCart;
      }

      // The broker's cache is filled by readSeedFromDom() on DOMContentLoaded, and this
      // script is `defer`, so it runs BEFORE that: on a fresh page load getCart() above is
      // still null and evaluate() would await fetchCart() for a cart that is already inline
      // in the document. Read that seed directly instead. It costs a JSON.parse and removes
      // a /cart.js round trip from every page load — which on the product page is the
      // difference between the progress bar painting during defer execution and painting a
      // second later, shoving the rest of the page down.
      //
      // Shape must match snippets/cart-state-seed.liquid (js/components/cart-state.js reads
      // the same tag); parsed defensively, since a bad seed must degrade to the fetch below
      // rather than take the whole manager down.
      if (!this._pendingCart) {
        var seedEl = document.getElementById('gv-cart-seed');
        if (seedEl) {
          try {
            var seed = JSON.parse(seedEl.textContent);
            if (seed && seed.cart && Array.isArray(seed.cart.items)) this._pendingCart = seed.cart;
          } catch (e) {
            // Malformed seed: leave _pendingCart unset so evaluate() fetches.
          }
        }
      }

      this.scheduleEvaluate(null, 0);
    }

    /* ------------------------------------------------------------------ */
    /*  Event hooks                                                        */
    /* ------------------------------------------------------------------ */

    onCartUpdate(payload) {
      if (this._suppressCartUpdates) return;
      // Reuse the cart payload Dawn already fetched so we don't fire another
      // /cart.js for every line-item change. The hook is called in three shapes:
      //   - window.updateCartGiftsStatus(parsedState)  -> payload IS the cart
      //   - subscribe('cart-update', {source, cartData}) -> cart is payload.cartData
      //   - bare onCartUpdate()                          -> no payload, will fetch
      var cart = null;
      if (payload) {
        if (Array.isArray(payload.items)) {
          cart = payload;
        } else if (payload.cartData && Array.isArray(payload.cartData.items)) {
          cart = payload.cartData;
        }
      }
      if (cart) this._pendingCart = cart;
      this.scheduleEvaluate(null, 100);
    }

    scheduleEvaluate(data, delay) {
      clearTimeout(this._debounceTimer);
      var ms = typeof delay === 'number' ? delay : 100;
      this._debounceTimer = setTimeout(function () {
        this.evaluate();
      }.bind(this), ms);
    }

    /* ------------------------------------------------------------------ */
    /*  Decision persistence                                               */
    /*                                                                     */
    /*  Persist the locked decision (keyed to the non-gift cart            */
    /*  fingerprint) so an unchanged cart skips re-deciding on the next     */
    /*  page load. Purely an optimisation: evaluate() measures in place and */
    /*  the enforcement loop already keeps an eligible gift across reloads, */
    /*  so nothing breaks if storage is unavailable and this is skipped.    */
    /* ------------------------------------------------------------------ */

    _persistDecision() {
      try {
        if (!this._decidedNonGiftFp || !this._decisions) return;
        // Store the chosen entry's index within its campaign group (entries are
        // plain JSON config objects), so we can rehydrate against the live config.
        var decided = {};
        var names = Object.keys(this._decisions);
        for (var i = 0; i < names.length; i++) {
          var name = names[i];
          var entry = this._decisions[name];
          var group = this.campaignGroups[name] || [];
          decided[name] = entry ? group.indexOf(entry) : -1;
        }
        window.localStorage.setItem(
          'gm_gift_decision_v1',
          JSON.stringify({ fp: this._decidedNonGiftFp, decided: decided })
        );
      } catch (e) {
        /* storage unavailable (private mode / quota) — non-fatal */
      }
    }

    _restoreDecision() {
      try {
        var raw = window.localStorage.getItem('gm_gift_decision_v1');
        if (!raw) return;
        var payload = JSON.parse(raw);
        if (!payload || typeof payload.fp !== 'string' || !payload.decided) return;
        var decisions = {};
        var names = Object.keys(payload.decided);
        for (var i = 0; i < names.length; i++) {
          var name = names[i];
          var group = this.campaignGroups[name];
          if (!group) continue; // campaign no longer configured — ignore
          var idx = payload.decided[name];
          decisions[name] = (typeof idx === 'number' && idx >= 0 && group[idx]) ? group[idx] : null;
        }
        this._decidedNonGiftFp = payload.fp;
        this._decisions = decisions;
      } catch (e) {
        /* corrupt or unavailable — fall back to a fresh decision */
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Core evaluation                                                    */
    /* ------------------------------------------------------------------ */

    async evaluate() {
      // A cart change that lands mid-evaluation must not be dropped: this run may be
      // deciding on a cart it read BEFORE that change, and nothing else would re-run it —
      // a gift stayed below its minimum spend until the next cart event. Remember the
      // request and run again once this pass finishes.
      if (this._evaluating) { this._rerunRequested = true; return; }
      this._evaluating = true;

      try {
        // Prefer the cart payload Dawn already fetched (set in onCartUpdate);
        // only hit /cart.js when we don't have a fresh one. Consumed once so a
        // re-evaluation after a gift add/remove always re-fetches a clean cart.
        var cart;
        if (this._pendingCart && Array.isArray(this._pendingCart.items)) {
          cart = this._pendingCart;
          this._pendingCart = null;
        } else {
          cart = await this.fetchCart();
        }
        var items = cart.items || [];
        var names = Object.keys(this.campaignGroups);

        // Anti-tamper: cap gift quantities before anything else.
        var tampered = await this.enforceGiftQuantity(items);
        if (tampered) {
          this._evaluating = false;
          this.scheduleEvaluate();
          return;
        }

        // Fingerprint ONLY the customer's own (non-gift) lines. The eligibility
        // decision is keyed to this, so the manager adding/removing a gift —
        // which leaves the non-gift lines unchanged — never re-triggers a
        // decision, which is what prevents any gift add/remove oscillation.
        var nonGiftFp = this.nonGiftFingerprint(items);

        if (nonGiftFp !== this._decidedNonGiftFp) {
          // The customer changed their real cart, so re-decide. We measure IN
          // PLACE — there is no need to remove an existing gift first:
          // buildCartMetrics excludes gift lines, and the order-discount scaling
          // it applies can never shrink the basis when a gift is added (see
          // cartDiscountRatio). So a gift already in the cart cannot lower the
          // result, and an already-eligible cart keeps its gift untouched
          // (no remove/re-add churn, no picker re-prompt). The enforcement loop
          // below removes a gift only when its campaign is no longer eligible.

          var metrics = this.buildCartMetrics(items, cart);
          this._decisions = {};
          this._failedAdds = {}; // new cart state — allow one fresh add attempt
          for (var n = 0; n < names.length; n++) {
            var entries = this.campaignGroups[names[n]];
            var metEntry = null;
            for (var e = 0; e < entries.length; e++) {
              if (this.conditionsMet(entries[e], items, metrics.giftEligibleTotal, metrics.vendorQty, metrics.handleQty, metrics.spendLines)) {
                metEntry = entries[e];
                break;
              }
            }
            this._decisions[names[n]] = metEntry;
          }
          this._decidedNonGiftFp = nonGiftFp;
          this._lastMetrics = metrics;
          // Persist so this decision survives a reload / cart re-entry and the
          // gift isn't stripped by a fresh re-decision next page load.
          this._persistDecision();
        } else if (this._decisions && this._revalidateHeldDecisions(names, items, cart)) {
          this._persistDecision();
        }

        // A gift VARIANT is given once per cart, whichever campaigns earn it. Two live
        // campaigns once shared one (Tiger Balm Fever Patch at $80 and at $130): each added
        // its own line, so a $130 cart got two. Claim variants in config order from the
        // lines held by campaigns that still earn their gift; a later campaign holding the
        // same variant is a duplicate, and one that needs it is already satisfied.
        var claimed = {};
        var duplicates = [];
        for (var c = 0; c < names.length; c++) {
          var held = this._decisions ? this._decisions[names[c]] : null;
          var line = held ? this.findGiftLine(names[c], items) : null;
          if (!line) continue;
          var claimedVariant = String(line.item.variant_id);
          if (claimed[claimedVariant]) duplicates.push(names[c]);
          else claimed[claimedVariant] = names[c];
        }

        // Enforce the locked decision. Idempotent: safe to run on every cart
        // event, and a no-op once the cart already matches the decision.
        for (var i = 0; i < names.length; i++) {
          var campaignName = names[i];
          var decision = this._decisions ? this._decisions[campaignName] : null;
          var existingGift = this.findGiftLine(campaignName, items);

          if (decision && existingGift && duplicates.indexOf(campaignName) !== -1) {
            this._giftOpsPending = true;
            await this.enqueue(this.removeGift.bind(this, campaignName, items));
          } else if (decision && !existingGift && this._giftVariantClaimed(decision, claimed)) {
            // Another campaign's line already gives this exact gift.
            this.removeGiftLoading(campaignName);
          } else if (decision && !existingGift) {
            if (decision.promotionType === 'customer_choice') {
              // A customer_choice gift is never auto-added — the customer must
              // pick it. So removing the gift (to swap it) or clearing and
              // refilling the cart always lets them choose afresh, rather than
              // forcing the previously selected gift back in.
              this.showPicker(campaignName, decision);
            } else if (this._deadGiftCampaigns[campaignName] ||
                       this._failedAdds[campaignName] === this._decidedNonGiftFp ||
                       !this._campaignHasStock(decision)) {
              // Skip the add when the gift can't be obtained: it already failed
              // (permanently out of stock or for this cart state), OR config
              // already says every variant is out of stock. Checking stock up
              // front avoids the doomed add.js request and the split-second
              // "Adding…" spinner flash that a known-OOS gift would otherwise
              // cause. Do NOT retry — that is what caused the loop/flood.
              // Remember it's dead so later cart events skip it too.
              if (!this._campaignHasStock(decision)) {
                this._deadGiftCampaigns[campaignName] = true;
              }
              // Remove any stale spinner without falsely showing "added to cart".
              this.removeGiftLoading(campaignName);
            } else {
              this._giftOpsPending = true;
              this.showGiftLoading(campaignName, decision);
              var addVariant = this._firstGiftVariantId(decision);
              if (addVariant) claimed[addVariant] = campaignName;
              await this.enqueue(this.addGift.bind(this, campaignName, decision));
            }
          } else if (!decision && existingGift) {
            this._giftOpsPending = true;
            this.showGiftRemoving(campaignName, existingGift.item);
            await this.enqueue(this.removeGift.bind(this, campaignName, items));
            this.hidePicker(campaignName);
          } else if (!decision) {
            this.hidePicker(campaignName);
          }
        }

        // Gift lines of a campaign no longer in the config (switched off, deleted, or
        // renamed onto a different gift) belong to none of the campaigns looped over
        // above, so nothing removed them before the checkout button's last check.
        var staleSeen = {};
        for (var s = 0; s < items.length; s++) {
          var staleName = this.campaignForLine(items[s]);
          if (!staleName || this.campaignGroups[staleName] || staleSeen[staleName]) continue;
          if (this._lineTotal(items[s]) !== 0) continue; // never a line the customer paid for
          staleSeen[staleName] = true;
          this._giftOpsPending = true;
          await this.enqueue(this.removeGift.bind(this, staleName, items));
        }

        var m = this._lastMetrics || this.buildCartMetrics(items, cart);
        this.updateProgressBanner(names, m);

      } catch (err) {
      } finally {
        this._evaluating = false;
        if (this._giftOpsPending) {
          this._giftOpsPending = false;
          this._enableCheckout();
        }
        if (this._rerunRequested) {
          this._rerunRequested = false;
          this.scheduleEvaluate(null, 0);
        }
      }
    }

    /**
     * Same customer lines, but a held "eligible" can still have LOST ground without the
     * fingerprint changing: the campaign's end date passed, the merchant raised the
     * threshold (a restored decision rehydrates against the LIVE config), or the same
     * lines got cheaper (login pricing, a discount). The fingerprint lock only guards
     * the ADD direction against oscillation, so re-check held decisions for removal.
     * buildCartMetrics excludes gift lines, so holding a gift can never make its own
     * campaign fail — this cannot oscillate. Returns true if any decision changed.
     */
    _revalidateHeldDecisions(names, items, cart) {
      var metrics = null;
      var changed = false;
      for (var i = 0; i < names.length; i++) {
        var held = this._decisions[names[i]];
        if (!held) continue;
        metrics = metrics || this.buildCartMetrics(items, cart);
        if (this.conditionsMet(held, items, metrics.giftEligibleTotal, metrics.vendorQty, metrics.handleQty, metrics.spendLines)) continue;
        var group = this.campaignGroups[names[i]] || [];
        var still = null;
        for (var g = 0; g < group.length; g++) {
          if (this.conditionsMet(group[g], items, metrics.giftEligibleTotal, metrics.vendorQty, metrics.handleQty, metrics.spendLines)) {
            still = group[g];
            break;
          }
        }
        this._decisions[names[i]] = still;
        changed = true;
      }
      if (changed) this._lastMetrics = metrics;
      return changed;
    }

    // The variant addGift would add for this campaign entry, as a string id, or null.
    _firstGiftVariantId(entry) {
      var options = (entry && entry.giftOptions) || [];
      var v = options.length && options[0].variants && options[0].variants[0];
      if (!v) return null;
      var id = v.numericId || this.extractNumericId(String(v.id));
      return id ? String(id) : null;
    }

    _giftVariantClaimed(entry, claimed) {
      if (entry.promotionType === 'customer_choice') return false; // the picker handles its own
      var id = this._firstGiftVariantId(entry);
      return !!(id && claimed[id]);
    }

    _lineTotal(item) {
      return (typeof item.final_line_price === 'number') ? item.final_line_price : (item.price || 0) * (item.quantity || 0);
    }

    /* ------------------------------------------------------------------ */
    /*  Condition evaluators                                               */
    /* ------------------------------------------------------------------ */

    // spendLines comes from buildCartMetrics; a caller that omits it fails a
    // qualifying_spend campaign closed (no qualifying spend → no gift).
    conditionsMet(campaign, cartItems, giftEligibleTotal, vendorQty, handleQty, spendLines) {
      if (!this.isWithinSchedule(campaign)) return false;
      if (campaign.conditionType === 'qualifying_products') {
        return this.getQualifyingQty(campaign, vendorQty, handleQty) >= (campaign.qualifyingMinQuantity || 1);
      }
      if (campaign.conditionType === 'qualifying_spend') {
        var spend = this.getQualifyingSpend(campaign, spendLines);
        // > 0 as well, so a campaign saved with no minimum still needs a
        // qualifying item instead of gifting every cart.
        return spend > 0 && spend >= (campaign.minimumSpend || 0);
      }
      return giftEligibleTotal >= (campaign.minimumSpend || 0);
    }

    isWithinSchedule(campaign) {
      var now = new Date();
      if (campaign.startDate && now < new Date(campaign.startDate)) return false;
      if (campaign.hasEndDate && campaign.endDate && now > new Date(campaign.endDate)) return false;
      return true;
    }

    // Shared: compute total qualifying qty for a campaign
    getQualifyingQty(campaign, vendorQty, handleQty) {
      var vendors = campaign.qualifyingVendors || [];
      var handles = campaign.qualifyingProductHandles || [];

      if (typeof vendors === 'string') {
        try { vendors = JSON.parse(vendors); } catch (_) { vendors = []; }
      }
      if (typeof handles === 'string') {
        try { handles = JSON.parse(handles); } catch (_) { handles = []; }
      }

      var qty = 0;
      var self = this;
      vendors.forEach(function (v) { qty += vendorQty[self.normalizeVendor(v)] || 0; });
      handles.forEach(function (h) { qty += handleQty[(h || '').toLowerCase()] || 0; });
      return qty;
    }

    // Dollar spend on a campaign's qualifying vendors/products (qualifying_spend).
    // Summed per line, so a line matching both lists counts once — unlike
    // getQualifyingQty, double-counting here would hand out gifts early. Uses the
    // discounted line totals from buildCartMetrics and ignores the shop-wide
    // vendor exclusions: listing a vendor here is an explicit choice.
    getQualifyingSpend(campaign, spendLines) {
      var vendors = campaign.qualifyingVendors || [];
      var handles = campaign.qualifyingProductHandles || [];

      if (typeof vendors === 'string') {
        try { vendors = JSON.parse(vendors); } catch (_) { vendors = []; }
      }
      if (typeof handles === 'string') {
        try { handles = JSON.parse(handles); } catch (_) { handles = []; }
      }

      var vendorSet = {};
      var handleSet = {};
      var self = this;
      vendors.forEach(function (v) { var n = self.normalizeVendor(v); if (n) vendorSet[n] = true; });
      handles.forEach(function (h) { var n = (h || '').toLowerCase(); if (n) handleSet[n] = true; });

      var spend = 0;
      (spendLines || []).forEach(function (l) {
        if (vendorSet[l.vendor] || handleSet[l.handle]) spend += l.total;
      });
      // Round to cents: summed dollar floats can land a hair under an exact threshold.
      return Math.round(spend * 100) / 100;
    }

    /* ------------------------------------------------------------------ */
    /*  Cart helpers                                                       */
    /* ------------------------------------------------------------------ */

    // Share of each line's price the customer still pays after ORDER-LEVEL
    // discounts. Shopify does not push an "amount off order" discount down into
    // line_item.final_line_price — it lives only in cart.total_price /
    // cart_level_discount_applications — so summing the lines measures the
    // PRE-discount spend (a $56.05 cart discounted to $50.45 still measured
    // $56.05). Scaling every line by total_price / sum(final_line_price) gives
    // each line its pro-rata share of the order discount, which keeps the
    // excluded-vendor subtraction consistent (both sides scale together) and
    // leaves shipping-only discounts alone (they never touch total_price).
    // Returns 1 — i.e. no scaling — whenever there is no cart, no lines, or
    // nothing but line-level discounts.
    //
    // Adding a gift cannot shrink this: a free gift line adds 0 to both sides,
    // a percentage order discount is invariant under adding any line, and a
    // fixed-amount one only moves the ratio toward 1. So an already-qualified
    // cart stays qualified and gifts cannot oscillate.
    cartDiscountRatio(items, cart) {
      if (!cart || typeof cart.total_price !== 'number') return 1;
      var lineSum = 0;
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        lineSum += (typeof it.final_line_price === 'number') ? it.final_line_price : (it.price * it.quantity);
      }
      if (!(lineSum > 0)) return 1;
      var ratio = cart.total_price / lineSum;
      if (!isFinite(ratio) || ratio < 0) return 1;
      return Math.min(1, ratio);
    }

    // Build cart totals/counters from non-gift items: eligibleTotal (full spend,
    // used for free shipping), hasColdChain, excludedTotal, giftEligibleTotal (eligibleTotal
    // minus excludedTotal, used for gift thresholds), excludedVendorNames,
    // vendorQty, handleQty, and spendLines (per-line discounted $, for
    // qualifying_spend). Pass `cart` (the whole payload, not just items)
    // so order-level discounts are folded in — see cartDiscountRatio.
    buildCartMetrics(items, cart) {
      var ratio = this.cartDiscountRatio(items, cart);
      var eligibleTotal = 0;
      var hasColdChain = false;
      var excludedTotal = 0;
      var excludedVendorNames = [];
      var seenExcluded = {};
      var vendorQty = {};
      var handleQty = {};
      var spendLines = [];
      var self = this;

      items.forEach(function (i) {
        if (self.campaignForLine(i) != null) return;
        if (i.properties && i.properties._gift_item === 'true') return;
        // Count the DISCOUNTED line total (final_line_price, then this line's
        // share of any order-level discount) so the threshold and the "X more to
        // unlock" banner match the cart subtotal the customer actually pays —
        // not the pre-discount price (i.price). Falls back to price × quantity
        // if final_line_price isn't present.
        var lineTotal = (typeof i.final_line_price === 'number') ? i.final_line_price : (i.price * i.quantity);
        if (ratio !== 1) lineTotal = Math.round(lineTotal * ratio);
        eligibleTotal += lineTotal / 100;

        // Cold-chain lines pay their own flat fee (a separate shipping profile)
        // that free shipping never waives. They still count toward the $ threshold:
        // Shopify measures the General profile's free-shipping condition on the
        // whole cart (verified with draftOrderCalculate, 2026-10-23).
        if (self.isColdChainLine(i)) hasColdChain = true;

        var v = self.normalizeVendor(i.vendor);

        // Excluded vendors stay in eligibleTotal because free shipping still
        // applies to them; only the gift basis subtracts excludedTotal.
        if (self.isExcludedVendor(i.vendor)) {
          excludedTotal += lineTotal / 100;
          if (v && !seenExcluded[v]) {
            seenExcluded[v] = true;
            excludedVendorNames.push(i.vendor);
          }
        }

        vendorQty[v] = (vendorQty[v] || 0) + i.quantity;
        var h = (i.handle || '').toLowerCase();
        handleQty[h] = (handleQty[h] || 0) + i.quantity;
        spendLines.push({ vendor: v, handle: h, total: lineTotal / 100 });
      });

      return {
        eligibleTotal: eligibleTotal,
        hasColdChain: hasColdChain,
        excludedTotal: excludedTotal,
        giftEligibleTotal: eligibleTotal - excludedTotal,
        excludedVendorNames: excludedVendorNames,
        vendorQty: vendorQty,
        handleQty: handleQty,
        spendLines: spendLines
      };
    }

    async fetchCart() {
      var res = await this.fetchWithRetry('/cart.js', { credentials: 'same-origin' });
      return res.json();
    }

    async fetchWithRetry(url, options, maxRetries) {
      maxRetries = maxRetries || 3;
      for (var attempt = 0; attempt <= maxRetries; attempt++) {
        var res = await fetch(url, options);
        // 429 = throttled; 409 = another cart write won the race (the cart page fires
        // several at load). Both are worth another try; neither is a refusal.
        if ((res.status === 429 || res.status === 409) && attempt < maxRetries) {
          await new Promise(function (resolve) {
            setTimeout(resolve, 1000 * (attempt + 1));
          });
          continue;
        }
        return res;
      }
    }

    // POST JSON to cart API with retry + suppression
    async postCart(url, body) {
      this._suppressCartUpdates = true;
      return this.fetchWithRetry(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      });
    }

    /**
     * The campaign a cart line belongs to, or null.
     *
     * `_gift_campaign` is authoritative when it is there — but it is not always there.
     * A cart restored from a Shopify Email "You left items in your cart" link
     * (`/cart?cart_link_id=…`) is rebuilt from variant ids and quantities alone, and
     * every line comes back stripped of its properties. Order #GP9378 shipped two free
     * samples exactly that way: the restored gift was invisible here, so evaluate() read
     * "eligible, no gift" and added a second one. Staff order edits and any other channel
     * drop the properties the same way.
     *
     * So the variant id is the fallback — GUARDED ON THE LINE BEING FREE. Gift products
     * are orderable (one gift variant is priced $999 in the catalog), and a line at a
     * price is a purchase whatever its variant id. Nothing in this file may delete or
     * cap a line the customer paid for, and this guard is what makes that true.
     *
     * The property is only authoritative while it names a campaign that STILL EXISTS.
     * Merchants rename campaigns in place — `free-lactokids-2-s-min-spend-100` became
     * "…$50" on 2026-09-10 — and every cart holding the gift keeps the old name. Trusting
     * it verbatim attributed those lines to a campaign no longer in the config, which is
     * invisible to the enforcement loop: the gift was never removed when the cart stopped
     * qualifying, and a second one was added when it qualified. So a stale name falls
     * through to the variant match, which re-points the line at the live campaign.
     */
    campaignForLine(item) {
      if (!item) return null;

      var tagged = item.properties && item.properties._gift_campaign;
      if (tagged && this.campaignGroups && this.campaignGroups[tagged]) return tagged;

      var name = this.giftVariants && this.giftVariants[String(item.variant_id)];
      if (name) {
        var lineTotal = (typeof item.final_line_price === 'number')
          ? item.final_line_price
          : (item.price || 0) * (item.quantity || 0);
        if (lineTotal === 0) return name;
      }

      // No live campaign claims this line. A stale name still marks it as a gift — that
      // is what makes validateAndCheckout strip a gift whose campaign has been deleted
      // outright, and what keeps it out of the qualifying totals.
      return tagged || null;
    }

    // Every line belonging to a campaign, propertied ones FIRST. Order matters to the
    // two callers: the propertied line is the one to keep (it is what marks the order
    // line as a gift for fulfilment), so the extras to cap or remove come after it.
    giftLinesFor(campaignName, items) {
      var propertied = [];
      var bare = [];
      for (var i = 0; i < items.length; i++) {
        if (this.campaignForLine(items[i]) !== campaignName) continue;
        var item = items[i];
        var entry = { item: item, index: i };
        if (item.properties && item.properties._gift_campaign) propertied.push(entry);
        else bare.push(entry);
      }
      return propertied.concat(bare);
    }

    findGiftLine(campaignName, items) {
      return this.giftLinesFor(campaignName, items)[0] || null;
    }

    cartFingerprint(items) {
      return items.map(function (i) {
        return i.variant_id + ':' + i.quantity + ':' + (i.properties && i.properties._gift_campaign || '');
      }).join('|');
    }

    // Fingerprint of the customer's own lines only — gift lines excluded — so a
    // gift being added/removed does not change it. The eligibility decision is
    // keyed to this value (see evaluate).
    nonGiftFingerprint(items) {
      var self = this;
      return items
        .filter(function (i) {
          if (self.campaignForLine(i) != null) return false;
          return !(i.properties && i.properties._gift_item === 'true');
        })
        .map(function (i) { return i.variant_id + ':' + i.quantity; })
        .join('|');
    }

    /* ------------------------------------------------------------------ */
    /*  Sequential cart operation queue                                    */
    /* ------------------------------------------------------------------ */

    enqueue(fn) {
      this._queue = this._queue.then(fn).catch(function (err) {
      });
      return this._queue;
    }

    /* ------------------------------------------------------------------ */
    /*  Cart operations                                                    */
    /* ------------------------------------------------------------------ */

    async addGift(campaignName, giftConfig) {
      var options = giftConfig.giftOptions || [];
      if (!options.length || !options[0].variants || !options[0].variants.length) {
        return;
      }
      var firstVariant = options[0].variants[0];
      var variantId = firstVariant.numericId || this.extractNumericId(String(firstVariant.id));
      if (!variantId) {
        return;
      }

      var res = await this.postCart('/cart/add.js', {
        items: [{
          id: variantId,
          quantity: 1,
          properties: { _gift_campaign: campaignName, _auto_added: 'true' }
        }]
      });

      // The gift add can be rejected by Shopify (e.g. 422 when the gift variant
      // is out of stock / unavailable). If we don't stop here, the enforce step
      // sees "qualified but no gift line" and retries forever.
      if (!res || !res.ok) {
        var status = res && res.status;
        if (status && status >= 400 && status < 500 && status !== 429 && status !== 409) {
          // Permanent client error (422 out of stock, invalid variant, etc.).
          // Stop attempting this gift for the session — no per-change retries.
          this._deadGiftCampaigns[campaignName] = true;
        } else {
          // Transient (429 rate-limit, 409 conflict, 5xx). Allow a retry on the next change.
          this._failedAdds[campaignName] = this._decidedNonGiftFp;
        }
        this._suppressCartUpdates = false;
        // The add failed — drop the spinner without claiming success.
        this.removeGiftLoading(campaignName);
        return;
      }

      this.hideGiftLoading(campaignName);
      this._lastCartToken = null;
      this.refreshCartUI();
    }

    /**
     * Address the line by its KEY, never by its index.
     *
     * `cartItems` is a SNAPSHOT. validateAndCheckout takes one cart, queues every gift
     * that must come out, then awaits removeGift once per gift — handing each call that
     * same stale array. Removing a line re-indexes the cart, so from the second removal
     * on `gift.index` points at the wrong line, or past the end:
     *
     *   422 {"status":"unprocessable_entity","description":"line parameter is invalid."}
     *
     * which matches no availability keyword and so reported as `cart_error INVALID |
     * change | 422` — 59 of them, always seconds after a successful `cart_updated
     * remove`. The 200 case is worse and silent: an off-by-one index still resolves, and
     * Shopify deletes whatever line is sitting there — a line the customer paid for.
     *
     * A key is immune to both. It survives re-indexing, and re-removing an already-gone
     * key is idempotent (200, verified against the live storefront), which is exactly the
     * behaviour a stale snapshot needs.
     */
    async removeGift(campaignName, cartItems) {
      // EVERY line the campaign owns, not just the first: a restored cart can hold a
      // second, propertyless copy of the same gift (see campaignForLine), and leaving it
      // behind is how an unearned gift reaches checkout.
      var lines = this.giftLinesFor(campaignName, cartItems).filter(function (l) {
        return !!l.item.key;
      });
      if (!lines.length) return;

      var res = null;
      for (var i = 0; i < lines.length; i++) {
        // Ask Shopify to render the page's sections as part of the removal, so the one
        // response carries the updated cart AND its HTML. assets/cart.js has always done
        // this for its own quantity edits; see _cartRenderSections for why it matters
        // beyond saving requests.
        var body = { id: lines[i].item.key, quantity: 0 };
        var sections = this._cartRenderSections();
        if (sections) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }

        res = await this.postCart('/cart/change.js', body);
      }

      this.hideGiftRemoving(campaignName);
      this._lastCartToken = null;
      this.refreshCartUI(await this._stateFromMutation(res));
    }

    /**
     * The section ids <cart-items> would repaint, or null if this page has none.
     *
     * Sending these with a cart mutation collapses four requests into one — the POST,
     * a /cart.js that re-reads what the POST already returned, a /cart?sections chained
     * behind it, and our own drawer fetch (folded in here).
     *
     * The requests are the smaller half. A separate read after a write paints whatever
     * the READ saw, not the result of the WRITE, so any cart change landing in that
     * window silently wins — a quantity click, cart-form-refresh, the prescription
     * gate's own removal. Rendering from the mutation's own response closes the window
     * entirely. It is the same stale-snapshot failure that produced
     * `cart_error INVALID | change | 422`, one layer up.
     *
     * getSectionsToRender() dereferences #main-cart-items and #main-cart-footer with no
     * null checks, so this is guarded: null simply means "take the read path".
     */
    _cartRenderSections() {
      var el = document.querySelector('cart-items');
      if (!el || typeof el.getSectionsToRender !== 'function') return null;
      try {
        var ids = el.getSectionsToRender().map(function (s) { return s.section; });
        ids.push('cart-drawer'); // rides along instead of costing its own request
        return ids.filter(function (id, i) { return id && ids.indexOf(id) === i; });
      } catch (e) {
        return null;
      }
    }

    /**
     * A mutation response usable as a render state, or null to fall back to fetching.
     *
     * Both halves or neither: renderFromCart recomputes a GATE-AWARE visible count by
     * reducing over `items`, so sections without a full cart would paint the right HTML
     * under the wrong empty-state. Shopify omits `sections` from error responses, which
     * makes this the live degradation path rather than a hypothetical one.
     *
     * This is also why only /cart/change.js gets this treatment. /cart/add.js answers
     * with the lines just ADDED, not the whole cart, so its `items` would describe the
     * gift alone — gift ADD still takes the read path, deliberately.
     */
    async _stateFromMutation(res) {
      if (!res || !res.ok || typeof res.json !== 'function') return null;
      try {
        var state = await res.json();
        if (state && state.sections && Array.isArray(state.items)) return state;
      } catch (e) { /* not JSON, or the body was already consumed */ }
      return null;
    }

    async addChosenGift(campaignName, variantId) {
      var numericId = this.extractNumericId(String(variantId));
      if (!numericId) numericId = parseInt(variantId, 10);
      if (!numericId) return;

      await this.postCart('/cart/add.js', {
        items: [{
          id: numericId,
          quantity: 1,
          properties: { _gift_campaign: campaignName, _auto_added: 'false' }
        }]
      });

      this.hidePicker(campaignName);
      this._lastCartToken = null;
      this.refreshCartUI();
    }

    /* ------------------------------------------------------------------ */
    /*  Quantity tampering protection                                      */
    /* ------------------------------------------------------------------ */

    /**
     * Cap each campaign at maxGiftsPerOrder ACROSS EVERY LINE it owns, not within one.
     *
     * The per-line reading is how #GP9378 shipped two free samples: both lines were qty
     * 1, so a quantity cap saw nothing to do. A campaign's allowance is a property of the
     * campaign, so it has to be spent across the lines, in giftLinesFor's order — the
     * propertied line first, so the copy that survives is the one the order records as a
     * gift, and the extras fall out.
     */
    async enforceGiftQuantity(items) {
      var tampered = false;
      var seen = {};

      for (var i = 0; i < items.length; i++) {
        var campaignName = this.campaignForLine(items[i]);
        if (!campaignName || seen[campaignName]) continue;
        seen[campaignName] = true;

        var maxQty = 1;
        var group = this.campaignGroups[campaignName];
        if (group) {
          group.forEach(function (c) {
            if (c.maxGiftsPerOrder > maxQty) maxQty = c.maxGiftsPerOrder;
          });
        }

        var lines = this.giftLinesFor(campaignName, items);
        var remaining = maxQty;
        for (var n = 0; n < lines.length; n++) {
          var item = lines[n].item;
          var allowed = Math.min(item.quantity, remaining > 0 ? remaining : 0);
          remaining -= allowed;

          // By key, for the same reason removeGift is. `items` here is whatever cart the
          // 100ms debounce captured, so it can already be behind the live cart by the time
          // this runs; an index read from it is a guess about a cart that has moved on.
          if (allowed !== item.quantity && item.key) {
            tampered = true;
            await this.postCart('/cart/change.js', { id: item.key, quantity: allowed });
          }
        }
      }

      if (tampered) this.refreshCartUI();
      return tampered;
    }

    /* ------------------------------------------------------------------ */
    /*  Gift picker UI (customer_choice)                                   */
    /* ------------------------------------------------------------------ */

    showPicker(campaignName, giftConfig) {
      var isCartPage = /^\/cart\b/.test(window.location.pathname);
      var containerId = isCartPage ? 'gift-picker-container' : 'gift-drawer-notifications';
      var container = document.getElementById(containerId);

      if (!container) {
        return;
      }

      var existingPicker = document.getElementById('gift-picker-' + campaignName);
      if (existingPicker) return;

      var options = giftConfig.giftOptions || [];
      if (!options.length) return;

      var picker = document.createElement('div');
      picker.id = 'gift-picker-' + campaignName;
      picker.className = 'gift-picker';
      picker.setAttribute('data-campaign', campaignName);

      var heading = giftConfig.pickerHeading || 'Choose your free gift!';
      var description = giftConfig.pickerDescription || '';

      var html = '<div class="gift-picker__header">';
      html += '<h3 class="gift-picker__heading">' + this.escapeHtml(heading) + '</h3>';
      if (description) {
        html += '<p class="gift-picker__description">' + this.escapeHtml(description) + '</p>';
      }
      html += '</div>';
      html += '<div class="gift-picker__grid">';

      var self = this;

      options.forEach(function (product) {
        html += '<div class="gift-picker__card" data-handle="' + self.escapeHtml(product.handle) + '">';

        html += '<div class="gift-picker__card-inner">';
        html += '<div class="gift-picker__image-wrapper">';
        if (product.imageUrl) {
          html += '<img class="gift-picker__image" src="' + self.escapeHtml(product.imageUrl) + '" alt="' + self.escapeHtml(product.title) + '" loading="lazy" width="300" height="300">';
        }
        html += '</div>';
        html += '<div class="gift-picker__badge"><span class="gift-picker__badge-free">Free Gift</span></div>';
        html += '</div>';

        html += '<div class="gift-picker__info">';
        html += '<p class="gift-picker__title">' + self.escapeHtml(product.title) + '</p>';
        html += '</div>';

        html += '<div class="gift-picker__button-container">';
        html += '<div class="gift-picker__price"><span class="gift-picker__price-free">FREE</span></div>';

        var variants = product.variants || [];
        var defaultVariantId = variants.length ? (variants[0].numericId || variants[0].id) : '';

        if (variants.length > 1) {
          html += '<select class="gift-picker__variant-select" data-campaign="' + self.escapeHtml(campaignName) + '">';
          variants.forEach(function (v) {
            html += '<option value="' + (v.numericId || v.id) + '">' + self.escapeHtml(v.title) + '</option>';
          });
          html += '</select>';
        }

        html += '<button type="button" class="gift-picker__select-btn" data-gv-cta="gift-pick" data-campaign="' + self.escapeHtml(campaignName) + '" data-variant-id="' + defaultVariantId + '">Select this gift</button>';
        html += '</div>';
        html += '</div>';
      });

      html += '</div>';
      picker.innerHTML = html;
      container.appendChild(picker);

      // Bind variant selectors
      picker.querySelectorAll('.gift-picker__variant-select').forEach(function (select) {
        select.addEventListener('change', function () {
          var btn = this.closest('.gift-picker__card').querySelector('.gift-picker__select-btn');
          if (btn) btn.setAttribute('data-variant-id', this.value);
        });
      });

      // Bind select buttons
      picker.querySelectorAll('.gift-picker__select-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var campaign = this.getAttribute('data-campaign');
          var card = this.closest('.gift-picker__card');
          var select = card.querySelector('.gift-picker__variant-select');
          var variantId = select ? select.value : this.getAttribute('data-variant-id');

          this.disabled = true;
          this.textContent = 'Adding...';
          self.addChosenGift(campaign, variantId);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      });

      if (isCartPage) this.showPickerCallout(campaignName);
    }

    showBanner(campaignName, giftConfig) {
      if (document.getElementById('gift-banner-' + campaignName)) return;

      var banner = document.createElement('div');
      banner.id = 'gift-banner-' + campaignName;
      banner.className = 'gift-picker__banner';
      banner.setAttribute('data-campaign', campaignName);

      var heading = giftConfig.pickerHeading || 'You qualify for a free gift!';
      banner.innerHTML = '<div class="gift-picker__banner-inner">' +
        '<span class="gift-picker__banner-text">' + this.escapeHtml(heading) + '</span>' +
        '<a href="/cart" class="gift-picker__banner-link">Choose your gift</a>' +
        '</div>';

      document.body.appendChild(banner);
    }

    hidePicker(campaignName) {
      var picker = document.getElementById('gift-picker-' + campaignName);
      if (picker) picker.remove();
      var banner = document.getElementById('gift-banner-' + campaignName);
      if (banner) banner.remove();
      this.hidePickerCallout(campaignName);
    }

    clearAllCallouts(preservePickerCallout) {
      var container = document.getElementById('main-cart-footer');
      if (!container) return;
      container.querySelectorAll(
        '.gift-picker__loading, [id^="gift-picker-callout-"], [id^="gift-loading-"], [id^="gift-removing-"]'
      ).forEach(function (el) {
        if (preservePickerCallout && el.id && el.id.indexOf('gift-picker-callout-') === 0) return;
        el.remove();
      });
    }

    hasPickerCallout() {
      var container = document.getElementById('main-cart-footer');
      return container && container.querySelector('[id^="gift-picker-callout-"]') !== null;
    }

    showPickerCallout(campaignName) {
      var calloutId = 'gift-picker-callout-' + campaignName;
      if (document.getElementById(calloutId)) return;

      this.clearAllCallouts();

      var container = document.getElementById('main-cart-footer');
      if (!container) return;

      var el = document.createElement('div');
      el.id = calloutId;
      el.className = 'gift-picker__loading gift-picker__loading--success';
      el.innerHTML = CHECKMARK_SVG +
        '<a href="#gift-picker-' + this.escapeHtml(campaignName) + '" class="gift-picker__callout-link">' +
          'You are eligible to select a free gift! Click here to choose.' +
        '</a>';

      el.querySelector('a').addEventListener('click', function (e) {
        e.preventDefault();
        var picker = document.getElementById('gift-picker-' + campaignName);
        if (picker) picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      container.insertBefore(el, container.firstChild);
    }

    hidePickerCallout(campaignName) {
      var el = document.getElementById('gift-picker-callout-' + campaignName);
      if (el) el.remove();
    }

    showGiftLoading(campaignName, giftConfig) {
      if (this.hasPickerCallout()) return;

      var loadingId = 'gift-loading-' + campaignName;
      if (document.getElementById(loadingId)) return;

      this.clearAllCallouts(true);

      var container = document.getElementById('main-cart-footer');
      if (!container) return;

      var giftName = this.decodeHtml(
        (giftConfig.giftOptions && giftConfig.giftOptions[0])
          ? giftConfig.giftOptions[0].title : 'free gift'
      );

      var el = document.createElement('div');
      el.id = loadingId;
      el.className = 'gift-picker__loading';
      el.setAttribute('data-gift-name', giftName);
      el.innerHTML = '<span class="gift-picker__loading-spinner"></span> Adding your ' + this.escapeHtml(giftName) + '...';
      container.insertBefore(el, container.firstChild);
    }

    hideGiftLoading(campaignName) {
      var el = document.getElementById('gift-loading-' + campaignName);
      if (!el) return;

      if (this.hasPickerCallout()) { el.remove(); return; }

      var giftName = this.decodeHtml(el.getAttribute('data-gift-name') || 'free gift');
      el.classList.add('gift-picker__loading--success');
      el.innerHTML = CHECKMARK_SVG + 'Congrats! ' + this.escapeHtml(giftName) + ' added to cart.';
    }

    // Remove the loading element without showing the "Congrats! ... added"
    // success message. Used when the gift add was rejected (e.g. 422 out of
    // stock) so we don't falsely claim a gift was added.
    removeGiftLoading(campaignName) {
      var el = document.getElementById('gift-loading-' + campaignName);
      if (el) el.remove();
    }

    showGiftRemoving(campaignName, cartItem) {
      if (this.hasPickerCallout()) return;

      this.clearAllCallouts(true);

      var container = document.getElementById('main-cart-footer');
      if (!container) return;

      var giftName = this.decodeHtml((cartItem && cartItem.title) || 'free gift');

      var el = document.createElement('div');
      el.id = 'gift-removing-' + campaignName;
      el.className = 'gift-picker__loading gift-picker__loading--removing';
      el.innerHTML = '<span class="gift-picker__loading-spinner"></span> Removing ' + this.escapeHtml(giftName) + ' from cart.';
      container.insertBefore(el, container.firstChild);
    }

    hideGiftRemoving(campaignName) {
      var el = document.getElementById('gift-removing-' + campaignName);
      if (el) el.remove();
    }

    /* ------------------------------------------------------------------ */
    /*  Progress banner                                                    */
    /* ------------------------------------------------------------------ */

    // Read the optional #free-shipping-config JSON block. Returns
    // { threshold, show:true } only when the merchant has configured a
    // positive threshold AND Liquid decided to show it (Singapore only).
    _readFreeShippingConfig() {
      var el = document.getElementById('free-shipping-config');
      if (!el) return null;
      try {
        var cfg = JSON.parse(el.textContent);
        if (!cfg || cfg.show !== true) return null;
        var threshold = parseFloat(cfg.threshold);
        if (!threshold || threshold <= 0) return null;
        return { threshold: threshold, show: true };
      } catch (e) {
        return null;
      }
    }

    // Shop-wide vendor exclusions for minimum_spend gift eligibility. Read from
    // #gift-settings-config, emitted by snippets/gift-campaigns-config.liquid.
    // A missing tag means "no exclusions", which is the pre-feature behaviour.
    _readExcludedVendors() {
      var set = new Set();
      var el = document.getElementById('gift-settings-config');
      if (!el) return set;
      try {
        var cfg = JSON.parse(el.textContent) || {};
        var list = cfg.excludedVendors || [];
        if (!Array.isArray(list)) list = [];
        for (var i = 0; i < list.length; i++) {
          var n = this.normalizeVendor(list[i]);
          if (n) set.add(n);
        }
      } catch (e) {
        // Malformed config must never take the gift engine down: fail open with
        // no exclusions rather than throwing out of init().
      }
      return set;
    }

    // Read #cold-chain-config ({ productIds, fee }), emitted by
    // snippets/gift-campaigns-config.liquid from the cold-chain collection.
    // Missing or malformed config means no cold-chain products.
    _readColdChainConfig() {
      var cfg = { ids: new Set(), fee: 0 };
      var el = document.getElementById('cold-chain-config');
      if (!el) return cfg;
      try {
        var raw = JSON.parse(el.textContent) || {};
        var list = Array.isArray(raw.productIds) ? raw.productIds : [];
        for (var i = 0; i < list.length; i++) cfg.ids.add(String(list[i]));
        cfg.fee = parseFloat(raw.fee) || 0;
      } catch (e) {}
      return cfg;
    }

    isColdChainLine(item) {
      if (!item || !this.coldChain || !this.coldChain.ids.size) return false;
      return this.coldChain.ids.has(String(item.product_id));
    }

    _coldChainNotice() {
      var fee = this.coldChain && this.coldChain.fee;
      return fee > 0
        ? 'Your order ships together by cold-chain same-day delivery for a flat S$' + fee.toFixed(2) + '.'
        : 'Your order ships together by cold-chain same-day delivery.';
    }

    // Vendor matching MUST stay identical to the Liquid rule in
    // snippets/gift-vendor-exclusion-notice.liquid. If the two drift, a vendor is
    // excluded from the cart total but shows no product-page notice, silently.
    isExcludedVendor(vendor) {
      if (!this.excludedVendors || !this.excludedVendors.size) return false;
      return this.excludedVendors.has(this.normalizeVendor(vendor));
    }

    // True if at least one gift variant for this campaign is purchasable.
    // A missing `available` field (older cached config) counts as in-stock, so
    // we never hide a banner just because the data isn't there yet.
    _campaignHasStock(campaign) {
      var options = (campaign && campaign.giftOptions) || [];
      if (!options.length) return false; // nothing configured to give
      for (var i = 0; i < options.length; i++) {
        var variants = options[i].variants || [];
        for (var j = 0; j < variants.length; j++) {
          if (variants[j].available !== false) return true;
        }
      }
      return false; // every configured variant is explicitly out of stock
    }

    // ROLLBACK: previous single-message renderer. To revert to the old
    // one-line banner: restore this body, uncomment showProgressBanner
    // below, and delete buildTierModel / renderProgressBar / _renderNotices.
    //
    // updateProgressBanner(campaignNames, eligibleTotal, vendorQty, handleQty) {
    //   var self = this;
    //   var sorted = campaignNames.slice().sort(function (a, b) {
    //     return ((self.campaignGroups[a][0] || {}).minimumSpend || 0) -
    //            ((self.campaignGroups[b][0] || {}).minimumSpend || 0);
    //   });
    //   var bestProgress = null;
    //   var bestName = null;
    //   for (var n = 0; n < sorted.length; n++) {
    //     var name = sorted[n];
    //     var entries = this.campaignGroups[name];
    //     var isMet = false;
    //     for (var e = 0; e < entries.length; e++) {
    //       if (this.conditionsMet(entries[e], null, eligibleTotal, vendorQty, handleQty)) { isMet = true; break; }
    //     }
    //     if (isMet) continue;
    //     if (this._deadGiftCampaigns[name] || !this._campaignHasStock(entries[0])) continue;
    //     if (!bestProgress) {
    //       var progress = this.computeProgress(entries[0], eligibleTotal, vendorQty, handleQty);
    //       if (progress.show) { bestProgress = progress; bestName = name; }
    //     }
    //   }
    //   var container = document.getElementById('gift-progress-container');
    //   if (!container) return;
    //   if (bestProgress) { this.showProgressBanner(bestName, bestProgress.message, bestProgress.imageUrl); }
    //   else { container.innerHTML = ''; }
    // }

    updateProgressBanner(campaignNames, metrics) {
      var model = this.buildTierModel(metrics);
      this.renderProgressBar(model.tiers, model.qtyNotices, metrics);
      this._announceProgress(model.tiers, metrics);
    }

    // Tell js/sections/analytics.js what the bar showed (gift_progress_viewed,
    // gift_tier_reached). Kind, threshold and lock state only: the gift's label and image
    // stay here. Never throws: analytics must not break the gift engine.
    _announceProgress(tiers, metrics) {
      try {
        var amountToNext = tiers.length ? this._buildStopsAndHeadline(tiers, metrics).remaining : null;
        document.dispatchEvent(new CustomEvent('gv:gift-progress', {
          detail: {
            tiers: tiers.map(function (t) { return { kind: t.kind, threshold: t.threshold, unlocked: t.unlocked }; }),
            amountToNext: amountToNext
          }
        }));
      } catch (e) {}
    }

    // Build the ordered milestone model from active minimum_spend campaigns
    // and the optional free-shipping tier. qualifying_products and
    // qualifying_spend campaigns do not map onto the cart-wide $-axis (one
    // counts units, the other a subset of the cart), so they become text
    // notices instead.
    buildTierModel(metrics) {
      var self = this;
      var eligibleTotal = metrics.eligibleTotal;
      var giftEligibleTotal = metrics.giftEligibleTotal;
      var vendorQty = metrics.vendorQty;
      var handleQty = metrics.handleQty;
      var tiers = [];
      var qtyNotices = [];
      var names = Object.keys(this.campaignGroups);

      for (var n = 0; n < names.length; n++) {
        var name = names[n];
        var entries = this.campaignGroups[name];

        // A campaign name can group multiple config entries. Handle the two
        // condition types independently so a group that mixes them isn't routed
        // to one path and silently dropped from the other.

        // qualifying_products / qualifying_spend → text notice (first
        // in-schedule such entry), worded by computeProgress.
        for (var q = 0; q < entries.length; q++) {
          var qp = entries[q];
          if (qp.conditionType !== 'qualifying_products' && qp.conditionType !== 'qualifying_spend') continue;
          if (!self.isWithinSchedule(qp)) continue;
          var prog = self.computeProgress(qp, giftEligibleTotal, vendorQty, handleQty, metrics.spendLines);
          if (prog.show && prog.message) qtyNotices.push(prog.message);
          break;
        }

        // minimum_spend gift tier. A campaign name can group multiple config
        // entries; evaluate() adds the gift as soon as ANY entry's threshold is
        // met (OR semantics), so the gift effectively unlocks at the LOWEST
        // in-schedule threshold. Use that entry for the node so the bar can't
        // show "locked" while the engine has already added the gift.
        var minEntry = null;
        for (var m = 0; m < entries.length; m++) {
          var en = entries[m];
          if (en.conditionType !== 'minimum_spend') continue;
          if (!self.isWithinSchedule(en)) continue;
          if (!minEntry || (en.minimumSpend || 0) < (minEntry.minimumSpend || 0)) minEntry = en;
        }
        if (!minEntry) continue;

        // Skip ones that can't be earned so we never advertise an
        // out-of-stock / dead campaign on the bar.
        if (self._deadGiftCampaigns[name] || !self._campaignHasStock(minEntry)) continue;

        // The node thumbnail is the first gift option's image for both promotion types — for a
        // customer_choice campaign that is the first card in the picker, so the bar previews a real
        // product instead of a generic gift icon. The label still differs: a choice campaign is
        // named by its picker heading, since no single option is "the" gift.
        var giftName = 'a free gift';
        var imageUrl = '';
        var firstOption = minEntry.giftOptions && minEntry.giftOptions.length ? minEntry.giftOptions[0] : null;
        if (firstOption && firstOption.imageUrl) imageUrl = firstOption.imageUrl;
        if (minEntry.promotionType === 'customer_choice') {
          if (minEntry.pickerHeading) giftName = self.decodeHtml(minEntry.pickerHeading);
        } else if (firstOption && firstOption.title) {
          giftName = self.decodeHtml(firstOption.title);
        }

        var threshold = minEntry.minimumSpend || 0;
        tiers.push({
          threshold: threshold,
          label: giftName,
          imageUrl: imageUrl,
          kind: 'gift',
          unlocked: giftEligibleTotal >= threshold,
          basis: 'gift'
        });
      }

      // Free-shipping tier (Singapore-only, gated in Liquid).
      if (this.freeShipping && this.freeShipping.show) {
        tiers.push({
          threshold: this.freeShipping.threshold,
          label: 'Free Shipping',
          imageUrl: '',
          kind: 'shipping',
          unlocked: eligibleTotal >= this.freeShipping.threshold,
          basis: 'full'
        });
      }

      tiers.sort(function (a, b) { return a.threshold - b.threshold; });
      return { tiers: tiers, qtyNotices: qtyNotices };
    }

    // Group tiers into stops (tiers sharing a threshold merge into one stop) and compute the
    // headline + maxThreshold. Pure. Shared by the cart-page renderProgressBar and the mini-cart
    // getMiniCartProgressHTML so the two never drift.
    _buildStopsAndHeadline(tiers, metrics) {
      var basisFor = function (t) { return t.basis === 'full' ? metrics.eligibleTotal : metrics.giftEligibleTotal; };
      var maxThreshold = tiers[tiers.length - 1].threshold || 0;
      var stops = [];
      for (var g = 0; g < tiers.length; g++) {
        var lastStop = stops[stops.length - 1];
        if (lastStop && lastStop.threshold === tiers[g].threshold) {
          lastStop.tiers.push(tiers[g]);
          lastStop.unlocked = lastStop.unlocked && tiers[g].unlocked;
        } else {
          stops.push({ threshold: tiers[g].threshold, tiers: [tiers[g]], unlocked: tiers[g].unlocked });
        }
      }
      var firstUnmet = null;
      for (var i = 0; i < stops.length; i++) {
        if (!stops[i].unlocked) { firstUnmet = stops[i]; break; }
      }
      var headline;
      if (firstUnmet) {
        var remaining = 0;
        for (var r = 0; r < firstUnmet.tiers.length; r++) {
          var rt = firstUnmet.tiers[r];
          if (rt.unlocked) continue;
          var short = rt.threshold - basisFor(rt);
          if (short > remaining) remaining = short;
        }
        if (remaining < 0) remaining = 0;
        // Free shipping is just another reward here: a shipping-only stop reads the same as a
        // gift stop, because _stopRewardLabel already returns "Free Shipping" for it. One
        // sentence for every locked state, one for every unlocked one. Shown on all three
        // surfaces (cart page, mini-cart dropdown, product page block).
        headline = 'Add S$' + remaining.toFixed(2) + ' more to your cart to unlock ' +
          this._stopRewardLabel(firstUnmet.tiers) + '!';
      } else {
        headline = 'All rewards unlocked! Checkout now!';
      }
      return { stops: stops, headline: headline, maxThreshold: maxThreshold, remaining: firstUnmet ? remaining : null };
    }

    // Build the .gift-progress-bar markup string. Pure. `fillPct` is the initial .gpb-fill width.
    _buildProgressBarMarkup(stops, qtyNotices, headline, maxThreshold, fillPct) {
      var html = '<div class="gift-progress-bar">';
      html += '<p class="gpb-headline">' + GIFT_SVG + '<span class="gpb-headline-text">' + headline + '</span></p>';
      html += '<div class="gpb-track-wrap">';
      html += '<div class="gpb-track"><div class="gpb-fill" style="width:' + fillPct.toFixed(2) + '%"></div></div>';
      for (var t = 0; t < stops.length; t++) {
        var stop = stops[t];
        var pos = maxThreshold > 0 ? (stop.threshold / maxThreshold) * 100 : 100;
        // Mark stops that include a free-shipping tier so a surface can style them apart — the
        // mini-cart shows the reward label ("Free Shipping") only under the shipping node and hides
        // it for gift nodes. Cart-page CSS does not target this class, so its bar is unaffected.
        var stopHasShipping = stop.tiers.some(function (t2) { return t2.kind === 'shipping'; });
        html += '<div class="gpb-node' + (stop.unlocked ? ' gpb-node--done' : '') + (stopHasShipping ? ' gpb-node--shipping' : '') + '" style="left:' + pos.toFixed(2) + '%">';
        html += '<span class="gpb-thumb">';
        for (var k = 0; k < stop.tiers.length; k++) {
          var st = stop.tiers[k];
          html += '<span class="gpb-thumb-item">';
          if (st.kind === 'shipping') {
            html += TRUCK_SVG;
          } else if (st.imageUrl) {
            html += '<img src="' + this.escapeHtml(st.imageUrl) + '" alt="" width="34" height="34">';
          } else {
            html += GIFT_SVG;
          }
          html += '</span>';
        }
        html += '</span>';
        html += '<span class="gpb-dot">' + (stop.unlocked ? CHECKMARK_SVG : '') + '</span>';
        html += '<span class="gpb-amount">' + this._formatAmount(stop.threshold) + '</span>';
        html += '<span class="gpb-reward">' + this._stopRewardLabelHTML(stop.tiers) + '</span>';
        html += '</div>';
      }
      html += '</div>'; // .gpb-track-wrap
      if (qtyNotices.length) html += this._renderNotices(qtyNotices);
      html += '</div>'; // .gift-progress-bar
      return html;
    }

    renderProgressBar(tiers, qtyNotices, metrics) {
      var container = document.getElementById('gift-progress-container');
      if (!container) return;

      // A cold-chain product page never advertises free shipping: its flat fee is never waived.
      if (container.hasAttribute && container.hasAttribute('data-hide-shipping-tier')) {
        tiers = tiers.filter(function (t) { return t.kind !== 'shipping'; });
      }

      var notices = this._noticesFor(qtyNotices, metrics, tiers);

      if (this._barSuppressed(tiers, metrics)) {
        this._lastFillPct = 0; // reset so the bar animates from 0 when it returns
        container.innerHTML = notices.length
          ? '<div class="gift-progress-bar">' + this._renderNotices(notices) + '</div>'
          : '';
        this._markContainerEmptiness(container);
        return;
      }

      // Start the fill at the previously rendered width (0 on first paint) so the CSS width
      // transition animates to the new target after the bar is inserted.
      var startFillPct = (typeof this._lastFillPct === 'number') ? this._lastFillPct : 0;

      var model = this._buildStopsAndHeadline(tiers, metrics);
      var stops = model.stops;
      var maxThreshold = model.maxThreshold;

      container.innerHTML = this._buildProgressBarMarkup(stops, notices, model.headline, maxThreshold, startFillPct);

      // On the narrow mobile track, spread any markers whose labels would collide.
      this._declutterMarkers(container);

      // Size the fill relative to the ACTUAL (possibly decluttered) marker positions.
      // Fill tracks the gift basis only when a gift tier is actually live; otherwise
      // (exclusions configured but no running campaign) it must track the full total,
      // matching the free-shipping-only bar it's rendering against.
      var fillTotal = tiers.some(function (t) { return t.kind === 'gift'; }) ? metrics.giftEligibleTotal : metrics.eligibleTotal;
      var fillPct = this._fillPctForMarkers(container, stops, fillTotal, maxThreshold);

      var fillEl = container.querySelector('.gpb-fill');
      if (fillEl) {
        void fillEl.offsetWidth; // force reflow to lock in the start width first
        fillEl.style.width = fillPct.toFixed(2) + '%';
      }
      this._lastFillPct = fillPct;
      this._markContainerEmptiness(container);
    }

    // The product-page container reserves the bar's height in CSS so the bar cannot shove
    // the variant picker and Add to Cart down when it paints (see .product-gift-progress in
    // assets/gift-manager.css). Hand that reservation back on the one path that legitimately
    // renders nothing — a cart whose every line is an excluded vendor — so the page is not
    // left with a permanent empty gap. Inert on the cart page, whose container never carries
    // the reserving class.
    _markContainerEmptiness(container) {
      if (!container || !container.classList) return;
      if (container.innerHTML === '') {
        container.classList.add('product-gift-progress--empty');
      } else {
        container.classList.remove('product-gift-progress--empty');
      }
    }

    // Static fill %: interpolate `total` across the stops' true positions (threshold/maxThreshold*100).
    // No DOM — for surfaces that render the bar statically (mini-cart). Matches the desktop fill.
    _staticFillPct(stops, total, maxThreshold) {
      if (maxThreshold <= 0 || !stops.length) return 0;
      var pts = [];
      for (var i = 0; i < stops.length; i++) {
        pts.push({ t: stops[i].threshold, p: (stops[i].threshold / maxThreshold) * 100 });
      }
      if (total <= pts[0].t) {
        return pts[0].t > 0 ? pts[0].p * (total / pts[0].t) : 0;
      }
      for (var j = 0; j < pts.length - 1; j++) {
        if (total <= pts[j + 1].t) {
          var frac = (total - pts[j].t) / (pts[j + 1].t - pts[j].t);
          return pts[j].p + (pts[j + 1].p - pts[j].p) * frac;
        }
      }
      return pts[pts.length - 1].p;
    }

    // Public: return the STATIC progress-bar HTML for a given cart, for surfaces that don't own the
    // #gift-progress-container (the mini-cart dropdown — see js/sections/mini-cart.js). Pure: no DOM
    // writes, no animation/declutter, no state mutation. Returns '' when gift-manager isn't booted
    // (no campaigns and no free-shipping tier) or the cart yields no spend tiers.
    getMiniCartProgressHTML(cart) {
      if (!this.campaignGroups) return ''; // init() returned early: nothing to show
      var items = (cart && cart.items) || [];
      var metrics = this.buildCartMetrics(items, cart);
      var model = this.buildTierModel(metrics);
      return this._progressBarHTML(model.tiers, model.qtyNotices, metrics);
    }

    // Fill width as a % of the track, interpolated across the ACTUAL marker
    // positions (which _declutterMarkers may have moved on mobile) rather than
    // raw dollars, so the fill lands exactly on a dot only when that dot's
    // threshold is genuinely met — and sits proportionally between dots
    // otherwise. On desktop, where dots sit at their true linear positions,
    // this is identical to the old eligibleTotal/maxThreshold formula.
    _fillPctForMarkers(container, stops, total, maxThreshold) {
      if (maxThreshold <= 0) return 0;
      var nodes = container.querySelectorAll('.gpb-node');
      if (!nodes.length) return 0;

      // Threshold ↔ (possibly decluttered) display position, in ascending order.
      var pts = [];
      for (var i = 0; i < nodes.length && i < stops.length; i++) {
        pts.push({ t: stops[i].threshold, p: parseFloat(nodes[i].style.left) || 0 });
      }

      // Below the first marker: from the track start (0% at $0) up to it.
      if (total <= pts[0].t) {
        return pts[0].t > 0 ? pts[0].p * (total / pts[0].t) : 0;
      }
      // Between two markers: proportional along that segment.
      for (var j = 0; j < pts.length - 1; j++) {
        if (total <= pts[j + 1].t) {
          var frac = (total - pts[j].t) / (pts[j + 1].t - pts[j].t);
          return pts[j].p + (pts[j + 1].p - pts[j].p) * frac;
        }
      }
      // At or beyond the top tier: pinned to its position (100%).
      return pts[pts.length - 1].p;
    }

    // Mobile-only cosmetic declutter: reward labels are a fixed width, so when
    // two thresholds sit close together (e.g. $80 and $100 on a $130 bar) their
    // names overlap. Enforce a minimum centre-to-centre spacing by walking the
    // markers right-to-left and pulling any that are too close to the next one
    // further left; the highest tier keeps its true position and crowding is
    // pushed back toward the start (clamped at 0). A moved dot no longer sits
    // exactly on its $ amount — acceptable, the labels stay legible. Generic:
    // no tier values are hardcoded, so it adapts to any gift/threshold set.
    _declutterMarkers(container) {
      if (!window.matchMedia('(max-width: 749.98px)').matches) return;
      var wrap = container.querySelector('.gpb-track-wrap');
      if (!wrap) return;
      var nodes = container.querySelectorAll('.gpb-node');
      if (nodes.length < 2) return;

      var trackW = wrap.clientWidth;
      if (!trackW) return; // hidden (e.g. closed drawer): re-runs on next render

      // Min centre gap = the mobile reward-label width (.gpb-reward @<=749.98px)
      // plus a little breathing room, expressed as a % of the track.
      var LABEL_W = 76, GAP = 8;
      var minGapPct = Math.min(100, (LABEL_W + GAP) / trackW * 100);

      var pos = [];
      for (var i = 0; i < nodes.length; i++) pos[i] = parseFloat(nodes[i].style.left) || 0;
      for (var j = nodes.length - 2; j >= 0; j--) {
        if (pos[j + 1] - pos[j] < minGapPct) {
          pos[j] = Math.max(0, pos[j + 1] - minGapPct);
        }
      }
      for (var k = 0; k < nodes.length; k++) nodes[k].style.left = pos[k].toFixed(2) + '%';
    }

    _renderNotices(notices) {
      // Notice strings come from computeProgress, which already escapes any
      // user-supplied gift/qualifier text.
      var html = '<div class="gpb-notices">';
      for (var i = 0; i < notices.length; i++) {
        html += '<p class="gpb-notice">' + notices[i] + '</p>';
      }
      html += '</div>';
      return html;
    }

    // "Abbott and Blackmores products don't count toward free gifts."
    // Names come from cart data and a merchant-editable metafield, and
    // _renderNotices concatenates raw HTML — so escape here, at the boundary.
    _excludedVendorNotice(names) {
      if (!names || !names.length) return '';
      var safe = [];
      for (var i = 0; i < names.length; i++) safe.push(this.escapeHtml(names[i]));
      var list;
      if (safe.length === 1) {
        list = safe[0];
      } else if (safe.length === 2) {
        list = safe[0] + ' and ' + safe[1];
      } else {
        list = safe.slice(0, -1).join(', ') + ' and ' + safe[safe.length - 1];
      }
      return list + " products don't count toward free gifts.";
    }

    // Shared by both surfaces: the qualifying_products notices plus the
    // excluded-vendor sentence, in that order. The exclusion sentence only
    // applies while a gift offer actually exists (gift tiers are built from
    // campaign config, not cart contents) — otherwise the sentence would
    // claim a brand is excluded from a gift that isn't running.
    _noticesFor(qtyNotices, metrics, tiers) {
      var notices = (qtyNotices || []).slice();
      var hasGiftTier = (tiers || []).some(function (t) { return t.kind === 'gift'; });
      if (hasGiftTier) {
        var note = this._excludedVendorNotice(metrics.excludedVendorNames);
        if (note) notices.push(note);
      }
      var hasShippingTier = (tiers || []).some(function (t) { return t.kind === 'shipping'; });
      if (hasShippingTier && metrics.hasColdChain) notices.push(this._coldChainNotice());
      return notices;
    }

    // A cart made up entirely of excluded items cannot progress any gift tier, so
    // the bar is suppressed and only the explanation remains. Also covers the
    // pre-existing "no tiers at all" case.
    //
    // Requires excludedTotal > 0 (not just giftEligibleTotal <= 0) so a cart that
    // is simply empty — or holds $0 of gift-eligible spend for reasons unrelated
    // to exclusions — is never suppressed when nothing is excluded. Without that
    // guard, landing on an empty cart page with a gift campaign configured would
    // blank the bar instead of showing "$X more to unlock ...!", which would
    // violate "bar exactly as today when nothing is excluded".
    _barSuppressed(tiers, metrics) {
      if (!tiers.length) return true;
      var hasGiftTier = tiers.some(function (t) { return t.kind === 'gift'; });
      return hasGiftTier && metrics.giftEligibleTotal <= 0 && metrics.excludedTotal > 0;
    }

    // Pure: the full static bar HTML. Used by the mini-cart, which does not animate.
    _progressBarHTML(tiers, qtyNotices, metrics) {
      var notices = this._noticesFor(qtyNotices, metrics, tiers);
      if (this._barSuppressed(tiers, metrics)) {
        return notices.length ? '<div class="gift-progress-bar">' + this._renderNotices(notices) + '</div>' : '';
      }
      var sh = this._buildStopsAndHeadline(tiers, metrics);
      // See renderProgressBar: fill only tracks the gift basis when a gift tier is live.
      var fillTotal = tiers.some(function (t) { return t.kind === 'gift'; }) ? metrics.giftEligibleTotal : metrics.eligibleTotal;
      var fillPct = this._staticFillPct(sh.stops, fillTotal, sh.maxThreshold);
      return this._buildProgressBarMarkup(sh.stops, notices, sh.headline, sh.maxThreshold, fillPct);
    }

    // Whole dollars render without cents ($40); fractional thresholds keep 2dp.
    _formatAmount(n) {
      return '$' + (n % 1 === 0 ? String(n) : n.toFixed(2));
    }

    // Combined reward name for the tiers sharing a stop's threshold, e.g.
    // "Free Shipping + Ultra Guard …". Free Shipping (if present) leads; gift
    // names follow, escaped. Joined with " + ".
    _stopRewardLabel(stopTiers) {
      var parts = [];
      if (stopTiers.some(function (t) { return t.kind === 'shipping'; })) {
        parts.push('Free Shipping');
      }
      for (var i = 0; i < stopTiers.length; i++) {
        if (stopTiers[i].kind === 'gift') parts.push(this.escapeHtml(stopTiers[i].label));
      }
      return parts.join(' + ');
    }

    // Same reward text as _stopRewardLabel, but as HTML: on a stop that combines free shipping AND
    // gift(s), the "+ gift" part is wrapped in .gpb-reward__extra so a surface can drop it. The
    // mini-cart hides .gpb-reward__extra under the shipping node to show just "Free Shipping"; the
    // cart page shows the whole label (the span is inert/unstyled there — visually identical). Used
    // for the node label only, not the headline.
    _stopRewardLabelHTML(stopTiers) {
      var hasShip = stopTiers.some(function (t) { return t.kind === 'shipping'; });
      var gifts = [];
      for (var i = 0; i < stopTiers.length; i++) {
        if (stopTiers[i].kind === 'gift') gifts.push(this.escapeHtml(stopTiers[i].label));
      }
      if (hasShip) {
        return 'Free Shipping' + (gifts.length ? '<span class="gpb-reward__extra"> + ' + gifts.join(' + ') + '</span>' : '');
      }
      return gifts.join(' + ');
    }

    computeProgress(campaign, eligibleTotal, vendorQty, handleQty, spendLines) {
      if (!this.isWithinSchedule(campaign)) return { show: false, message: '' };

      var giftName = 'a free gift';
      var giftImageUrl = '';
      if (campaign.giftOptions && campaign.giftOptions.length > 0 && campaign.promotionType !== 'customer_choice') {
        if (campaign.giftOptions[0].title) {
          giftName = this.escapeHtml(this.decodeHtml(campaign.giftOptions[0].title));
        }
        if (campaign.giftOptions[0].imageUrl) {
          giftImageUrl = campaign.giftOptions[0].imageUrl;
        }
      }

      if (campaign.conditionType === 'qualifying_products') {
        var minQty = campaign.qualifyingMinQuantity || 1;
        var qty = this.getQualifyingQty(campaign, vendorQty, handleQty);

        if (qty < 1 || qty >= minQty) return { show: false, message: '' };

        var remaining = minQty - qty;
        var vendors = campaign.qualifyingVendors || [];
        var handles = campaign.qualifyingProductHandles || [];
        var qualifier = 'qualifying';
        if (typeof vendors === 'string') {
          try { vendors = JSON.parse(vendors); } catch (_) { vendors = []; }
        }
        if (vendors.length === 1 && (!handles || handles.length === 0)) {
          qualifier = this.decodeHtml(vendors[0]);
        }

        var msg = 'Add ' + remaining + ' more ' + this.escapeHtml(qualifier) +
          ' item' + (remaining > 1 ? 's' : '') + ' to unlock ' + giftName + '!';
        return { show: true, message: msg, imageUrl: giftImageUrl };
      }

      if (campaign.conditionType === 'qualifying_spend') {
        var spend = this.getQualifyingSpend(campaign, spendLines);
        var spendThreshold = campaign.minimumSpend || 0;
        // Like qualifying_products: silent until a qualifying item is in the cart.
        if (spend <= 0 || spend >= spendThreshold) return { show: false, message: '' };

        var spendVendors = campaign.qualifyingVendors || [];
        var spendHandles = campaign.qualifyingProductHandles || [];
        if (typeof spendVendors === 'string') {
          try { spendVendors = JSON.parse(spendVendors); } catch (_) { spendVendors = []; }
        }
        if (typeof spendHandles === 'string') {
          try { spendHandles = JSON.parse(spendHandles); } catch (_) { spendHandles = []; }
        }
        var spendQualifier = 'qualifying items';
        if (spendVendors.length === 1 && (!spendHandles || spendHandles.length === 0)) {
          spendQualifier = this.decodeHtml(spendVendors[0]);
        }

        var spendMsg = 'Spend $' + (spendThreshold - spend).toFixed(2) + ' more on ' +
          this.escapeHtml(spendQualifier) + ' to unlock ' + giftName + '!';
        return { show: true, message: spendMsg, imageUrl: giftImageUrl };
      }

      // minimum_spend
      var threshold = campaign.minimumSpend || 0;
      if (eligibleTotal >= threshold) return { show: false, message: '' };

      var remaining = (threshold - eligibleTotal).toFixed(2);
      var msg = '$' + remaining + ' more to unlock ' + giftName + '!';
      return { show: true, message: msg, imageUrl: giftImageUrl };
    }

    // ROLLBACK: single-line banner renderer, unused by the milestone bar.
    // Uncomment together with the old updateProgressBanner body above to revert.
    // showProgressBanner(campaignName, message, imageUrl) {
    //   var bannerId = 'gift-progress-' + campaignName;
    //
    //   var container = document.getElementById('gift-progress-container');
    //   if (!container) return;
    //
    //   var existing = document.getElementById(bannerId);
    //   if (existing) {
    //     var msgEl = existing.querySelector('.gift-picker__progress-text');
    //     if (msgEl && msgEl.innerHTML !== message) msgEl.innerHTML = message;
    //     return;
    //   }
    //
    //   container.innerHTML = '';
    //
    //   var thumbHtml = '';
    //   if (imageUrl) {
    //     thumbHtml = '<img src="' + imageUrl + '" alt="" width="40" height="40" style="width:40px;height:40px;object-fit:cover;border-radius:4px;flex-shrink:0">';
    //   }
    //
    //   var el = document.createElement('div');
    //   el.id = bannerId;
    //   el.className = 'tw-w-full tw-py-m tw-px-l tw-bg-primary-50 tw-border tw-border-[#E3E3E3] tw-border-b-primary-100 tw-rounded-t-sm';
    //   el.innerHTML =
    //     '<div class="tw-flex tw-flex-row tw-justify-center tw-items-center tw-gap-s tw-w-full">' +
    //       thumbHtml +
    //       '<span class="tw-text-headingM tw-font-semiBold tw-text-primary-500 tw-font-heading gift-picker__progress-text" style="font-size:1.5rem">' +
    //         message +
    //       '</span>' +
    //     '</div>';
    //   container.appendChild(el);
    // }

    /* ------------------------------------------------------------------ */
    /*  UI refresh                                                         */
    /* ------------------------------------------------------------------ */

    /**
     * @param prefetched a mutation response already carrying { ...cart, sections }
     *   (see _stateFromMutation), or null/undefined to go and fetch.
     */
    refreshCartUI(prefetched) {
      var self = this;
      var isCartPage = /^\/cart\b/.test(window.location.pathname);

      // Nothing left to ask for: the mutation came back with the cart and its rendered
      // sections. Paint and finish.
      if (prefetched) {
        var el = document.querySelector('cart-items');
        if (el && typeof el.renderFromCart === 'function') {
          var painted = false;
          try {
            el.renderFromCart(prefetched);
            painted = true;
          } catch (e) {
            painted = false; // fall through to the fetch path rather than paint nothing
          }
          if (painted) {
            var prefetchedDrawer = document.querySelector('cart-drawer');
            if (prefetchedDrawer
              && typeof prefetchedDrawer.renderContents === 'function'
              && prefetched.sections['cart-drawer']) {
              prefetchedDrawer.renderContents({ sections: prefetched.sections });
            }
            this._settleAfterRender();
            this._updateCartCount();
            return;
          }
        }
      }

      // The cart page repaint belongs to <cart-items> (assets/cart.js), not here.
      //
      // This used to fetch the main-cart-items section and copy TWO things out of it —
      // `.js-contents` innerHTML and the subtotal text — discarding everything else,
      // including the `is-empty` class the server had just computed on the section's
      // root <cart-items> (`gv_cart_visually_empty`, sections/main-cart-items.liquid).
      //
      // CSS shows `.cart__warnings` ("Your Cart is Empty") only under `.is-empty`, and
      // hides `.cart__contents-wrapper` only under `.is-empty`. So when removing a gift
      // emptied the cart, the rows went away, the class never arrived, and BOTH ended up
      // invisible: a blank panel over a $0.00 subtotal and a dead Checkout button, until
      // the customer reloaded and got the class rendered server-side.
      //
      // onCartUpdate re-renders the contents, the warnings, the footer and the icon
      // bubble, runs the fail-closed gate, and applies a GATE-AWARE `is-empty` — which a
      // local `item_count === 0` could not, since a logged-out cart holding only
      // prescription/pharmacy lines is visually empty while item_count is not zero.
      // Delegating keeps that rule in ONE place instead of adding a third answer to it.
      //
      // Guarded because onCartUpdate does its section bookkeeping SYNCHRONOUSLY —
      // getSectionsToRender() dereferences #main-cart-items and #main-cart-footer without
      // null checks, so a cart page missing either (a merchant removing the footer section
      // is enough) throws straight back out here. Un-caught, that would skip the fetch
      // below as well and leave the cart painted with nothing at all. On a throw, fall
      // through to the hand patch, which is exactly what this path is for.
      var cartItems = isCartPage ? document.querySelector('cart-items') : null;
      var delegatePage = !!(cartItems && typeof cartItems.onCartUpdate === 'function');
      if (delegatePage) {
        try {
          cartItems.onCartUpdate({ source: 'gift-manager' });
        } catch (e) {
          delegatePage = false;
        }
      }

      // Only still needed for the drawer, plus the hand patch below when the custom
      // element is missing or has not upgraded.
      var sectionsToFetch = (isCartPage && !delegatePage) ? 'main-cart-items,cart-drawer' : 'cart-drawer';

      this.fetchWithRetry('/cart?sections=' + sectionsToFetch, { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          if (isCartPage && !delegatePage && sections['main-cart-items']) {
            var doc = new DOMParser().parseFromString(sections['main-cart-items'], 'text/html');
            var newContent = doc.querySelector('.js-contents');
            var oldContent = document.querySelector('.js-contents');
            if (newContent && oldContent) oldContent.innerHTML = newContent.innerHTML;

            var newTotals = doc.querySelector('.totals__subtotal-value');
            var oldTotals = document.querySelector('.totals__subtotal-value');
            if (newTotals && oldTotals) oldTotals.textContent = newTotals.textContent;

            // Mirror the class the server computed, so the fallback path is not the
            // bug this whole comment is about.
            var newRoot = doc.querySelector('cart-items');
            var oldRoot = document.querySelector('cart-items');
            if (newRoot && oldRoot) {
              oldRoot.classList.toggle('is-empty', newRoot.classList.contains('is-empty'));
            }
          }

          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function' && sections['cart-drawer']) {
            drawer.renderContents({ sections: sections });
          }

          self._settleAfterRender();
        })
        .catch(function () {
          self._suppressCartUpdates = false;
          self.scheduleEvaluate(null, 100);
          if (isCartPage) window.location.reload();
        });

      // A /cart.js fired 800ms after the section render used to live here — a
      // blind, arbitrarily-delayed extra read racing the render it was meant to
      // follow. The delay existed to let the mutation "settle" first; the broker
      // now gives us an event for that instead of a guess. See _updateCartCount().
      self._updateCartCount();
    }

    /**
     * Re-open the gate after a repaint. ONE copy, shared by both render paths, so the
     * two can never drift into disagreeing about when a cart change is safe to act on.
     */
    _settleAfterRender() {
      var self = this;
      this._enableCheckout();
      setTimeout(function () {
        self._suppressCartUpdates = false;
        // Re-evaluate to catch any cart changes that were suppressed
        self.scheduleEvaluate(null, 100);
      }, 500);
    }

    /* ------------------------------------------------------------------ */
    /*  Checkout button                                                    */
    /* ------------------------------------------------------------------ */

    _enableCheckout() {
      var fn = this._origUpdateCheckout;
      if (!fn) return;
      var formType = window.currentCartFormType;
      if (typeof formType === 'undefined') formType = 'None';

      // Build the item list from the REAL cart (the shared cart-state broker), NOT the DOM.
      // For a logged-out shopper the server hides restricted (prescription/pharmacy) rows
      // (main-cart-items.liquid {% continue %}), so a DOM scrape misses a restricted line still
      // in the real cart (removal pending / failed / 429) — gvHasRestrictedLine then reads false
      // and the fail-closed gate (updateCheckoutButtonState -> gvMustForceDisable) is bypassed,
      // re-enabling checkout. The broker holds the raw cart (cart-state-seed.liquid seeds the full
      // {{ cart | json }}), so it sees the restricted line. Same fail-open class as commit 914e8cc.
      var cartItems = [];
      var realCart = (window.__gvCartState && typeof window.__gvCartState.getCart === 'function')
        ? window.__gvCartState.getCart()
        : null;
      if (realCart && Array.isArray(realCart.items)) {
        cartItems = realCart.items.map(function (item) {
          return { product_type: item.product_type || '' };
        });
      } else {
        // Fallback: broker unavailable — preserve the prior DOM-scrape behaviour.
        document.querySelectorAll('.cart-item').forEach(function (item) {
          cartItems.push({ product_type: item.getAttribute('data-product-type') || '' });
        });
      }
      fn(cartItems, formType);
    }

    // Updates the header cart-count bubble and broadcasts the post-gift-operation
    // cart via `cartFormUpdate`. refreshCartUI() re-renders the cart page and cart
    // drawer sections directly, but every OTHER surface (mini cart — see
    // js/sections/mini-cart.js — and the React cart-form widgets mounted through
    // js/components/react-wrapper.js) learns about cart changes only through this
    // event, so a gift added or removed here would otherwise be invisible to them.
    //
    // Every gift add/remove goes through /cart/add.js or /cart/change.js, which
    // js/sections/standard-actions.js's fetch bridge already intercepts and reads
    // freshly (readCart({force:true}) in emitWithFreshCart), publishing the result
    // to the shared cart-state broker (js/components/cart-state.js, exposed to
    // this hand-maintained vanilla file as window.__gvCartState). Subscribing to
    // that reuses the read the bridge already makes for the SAME mutation instead
    // of firing our own — this used to be a /cart.js fetch on a blind 800ms delay.
    _updateCartCount() {
      var apply = function (cart) {
        if (!cart) return;
        document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        document.querySelectorAll('.cart-count-bubble .visually-hidden').forEach(function (el) {
          el.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
        });
        document.dispatchEvent(new CustomEvent('cartFormUpdate', { detail: { cart: cart } }));
      };

      if (window.__gvCartState && typeof window.__gvCartState.subscribe === 'function') {
        var applied = false;
        var off = window.__gvCartState.subscribe(function (cart) {
          if (applied) return;
          applied = true;
          off();
          apply(cart);
        });
        // Defensive per HAZARDS: the broker should always be present by the time a
        // gift operation completes (standard-actions.js loads before this file —
        // see layout/theme.liquid — and every mutation republishes to it), but if
        // nothing notifies within a reasonable window, fall back to a direct read
        // so the bubble/broadcast do not silently go stale.
        setTimeout(function () {
          if (applied) return;
          applied = true;
          off();
          this.fetchWithRetry('/cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(apply)
            .catch(function () {});
        }.bind(this), 2000);
        return;
      }

      this.fetchWithRetry('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(apply)
        .catch(function () {});
    }

    /* ------------------------------------------------------------------ */
    /*  Checkout interception                                              */
    /* ------------------------------------------------------------------ */

    interceptCheckout() {
      var self = this;
      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (form.id !== 'cart' && form.id !== 'CartDrawer-Form') return;
        var submitter = e.submitter;
        if (!submitter || submitter.name !== 'checkout') return;

        e.preventDefault();
        submitter.disabled = true;
        submitter.textContent = 'Validating...';
        self.validateAndCheckout(submitter);
      }, true);
    }

    async validateAndCheckout(submitButton) {
      try {
        var cart = await this.fetchCart();
        var items = cart.items || [];
        var metrics = this.buildCartMetrics(items, cart);

        var removals = [];
        var seen = {};
        for (var i = 0; i < items.length; i++) {
          // campaignForLine, not the property alone: a propertyless copy left by a cart
          // restore is still a gift, and this is the last gate before /checkout.
          // Once per campaign — removeGift now takes out every line the campaign owns.
          var campaignName = this.campaignForLine(items[i]);
          if (!campaignName || seen[campaignName]) continue;
          seen[campaignName] = true;

          var group = this.campaignGroups[campaignName];
          if (!group) {
            removals.push({ campaignName: campaignName, items: items });
            continue;
          }

          var met = false;
          for (var e = 0; e < group.length; e++) {
            if (this.conditionsMet(group[e], items, metrics.giftEligibleTotal, metrics.vendorQty, metrics.handleQty, metrics.spendLines)) {
              met = true;
              break;
            }
          }
          if (!met) removals.push({ campaignName: campaignName, items: items });
        }

        if (removals.length > 0) {
          for (var r = 0; r < removals.length; r++) {
            await this.removeGift(removals[r].campaignName, removals[r].items);
          }
          this._lastCartToken = null;
          this.refreshCartUI();
          submitButton.disabled = false;
          submitButton.textContent = submitButton.getAttribute('data-original-text') || 'Check out';
        } else {
          window.location.href = '/checkout';
        }
      } catch (err) {
        window.location.href = '/checkout';
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Utilities                                                          */
    /* ------------------------------------------------------------------ */

    extractNumericId(gid) {
      if (!gid) return null;
      var match = String(gid).match(/(\d+)$/);
      return match ? parseInt(match[1], 10) : null;
    }

    normalizeVendor(str) {
      return (str || '').toLowerCase().replace(/\s*[&+]\s*/g, ' and ').replace(/\s+/g, ' ').trim();
    }

    decodeHtml(str) {
      if (!str) return '';
      var txt = document.createElement('textarea');
      txt.innerHTML = str;
      return txt.value;
    }

    escapeHtml(str) {
      if (!str) return '';
      var div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  /* -------------------------------------------------------------------- */
  /*  Boot                                                                 */
  /* -------------------------------------------------------------------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      window._giftManager = new GiftManager();
    });
  } else {
    window._giftManager = new GiftManager();
  }
})();
